/**
 * @file EmploymentStatus.js
 * @description Card for the Employment Status dashboard.
 */

import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { DASHBOARD_AS_OF_DATE_FORMAT } from './DashboardUtils';
import NotEnoughData from './NotEnoughData';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';
import './EmploymentStatus.scss';
import { getText } from '../../i18n';

const mapApiResponseToEmploymentStatusData = dataFromApi => {
    const counts = {
        Active: 0,
        LeaveOfAbsence: 0,
        Terminated: 0,
    };

    dataFromApi.forEach(({ employmentStatus, count }) => {
        switch (employmentStatus) {
            case 'Active':
                counts.Active += count;
                break;
            case 'LeaveOfAbsence':
                counts.LeaveOfAbsence += count;
                break;
            case 'Terminated':
                counts.Terminated += count;
                break;
            default:
                break;
        }
    });

    return [
        { name: 'Active', value: counts.Active, color: '#8BE78B' },
        {
            name: 'Leave of Absence',
            value: counts.LeaveOfAbsence,
            color: '#4E8EFF',
        },
        { name: 'Terminated', value: counts.Terminated, color: '#F95C5C' },
    ];
};

const isEmploymentStatusDataEmpty = data => {
    if (!Array.isArray(data) || data.length === 0) return true;
    return data.every(item => 'count' in item && item.count === 0);
};

const EmploymentStatus = ({ employmentStatusData, endDate }) => {
    if (isEmploymentStatusDataEmpty(employmentStatusData)) {
        return <NotEnoughData />;
    }

    const [chartData, setChartData] = useState([]);
    const formattedAsOfDate = format(
        new Date(endDate),
        DASHBOARD_AS_OF_DATE_FORMAT,
    );
    const total = chartData?.reduce((sum, entry) => sum + entry.value, 0);

    useEffect(() => {
        setChartData(
            mapApiResponseToEmploymentStatusData(employmentStatusData),
        );
    }, [employmentStatusData, endDate]);

    return (
        <div className="employment-status">
            <div className="employment-status-date-subtitle">
                {getText('msg_a8770f2d4d18')} {formattedAsOfDate}
            </div>
            <div className="employment-chart-wrapper">
                <div className="employment-status-chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (
                                        active &&
                                        payload &&
                                        payload.length > 0
                                    ) {
                                        const { name, value } =
                                            payload[0].payload;
                                        return (
                                            <CustomDashboardTooltip
                                                active={active}
                                                label={name}
                                                value={value}
                                                unit="employees"
                                            />
                                        );
                                    }
                                    return null;
                                }}
                                wrapperStyle={{ zIndex: 1 }}
                            />

                            <Pie
                                data={chartData}
                                innerRadius={90}
                                outerRadius={115}
                                dataKey="value"
                                startAngle={-180}
                                endAngle={180}
                                stroke="none"
                            >
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="employment-status-chart-center">
                        <div className="employment-status-total">
                            {total.toLocaleString()}
                        </div>
                        <div className="employment-status-label">{getText('msg_5a537e209151')}</div>
                    </div>
                </div>

                <div className="employment-status-legend">
                    {chartData.map((entry, index) => {
                        const percentage = (
                            (entry.value / total) *
                            100
                        ).toFixed(1);
                        return (
                            <div
                                key={index}
                                className="employment-status-legend-item"
                            >
                                <div
                                    className="employment-status-legend-dot"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <div className="employment-status-legend-text">
                                    <div className="employment-status-legend-label">
                                        {entry.name}
                                    </div>
                                    <div className="employment-status-legend-value">
                                        {entry.value.toLocaleString()} (
                                        {percentage}%)
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default EmploymentStatus;
